import { BigNumber } from "@yjsgames/bignumber";
import type { RewardBundle } from "../models/RewardSpec.js";
import type { ModuleDrop, RandomSource, RarityDef } from "./GoldModuleBox.js";

/**
 * 해석된 보상 — RewardBundle을 종류별로 합산/추첨한 결과 (순수). 지급은 호출자가 분배한다.
 * artifacts는 유물 시스템 미구현이라 미지급 — 표시/로깅용으로만 담는다(placeholder).
 */
export interface ResolvedReward {
  gold: BigNumber;
  diamond: BigNumber;
  rebirthPoint: number;
  upgradeStone: number;
  /** 획득 모듈 — (id, 추첨된 등급). 등급 먼저 추첨 후 등급 내 균등 선택(GoldModuleBox와 동일 알고리즘). */
  moduleDrops: ModuleDrop[];
  artifacts: { id: string; amount: number }[];
}

export interface ResolveContext {
  /** 0~1 난수 소스 — 주입으로 테스트 결정론 확보. */
  readonly rng: RandomSource;
  /** 등급 추첨 풀 (id, rarity) — expandedModulePool(ALL_MODULE_DEFINITIONS) 매핑(모듈이 등급별로 펼쳐짐). */
  readonly rarityDefs: readonly RarityDef[];
}

/**
 * 등급 가중 균등 모듈 픽 (GoldModuleBox.pickWeighted와 동일 알고리즘 — 등급 확률 ÷ 등급 내 엔트리 수).
 * 풀이 (id, rarity)로 펼쳐져 있어 추첨된 등급이 함께 반환된다. 풀이 비거나 가중치 합 0이면 null. 단일 rng 호출.
 */
export function pickWeightedModule(
  pool: readonly RarityDef[],
  weights: Record<string, number>,
  rng: RandomSource,
): ModuleDrop | null {
  if (pool.length === 0) return null;
  const counts = new Map<string, number>();
  for (const d of pool) counts.set(d.rarity, (counts.get(d.rarity) ?? 0) + 1);
  const weighted = pool.map((d) => ({
    drop: { id: d.id, rarity: d.rarity },
    w: (weights[d.rarity] ?? 0) / (counts.get(d.rarity) ?? 1),
  }));
  const total = weighted.reduce((s, e) => s + e.w, 0);
  if (total <= 0) return null;
  let roll = rng() * total;
  for (const e of weighted) {
    roll -= e.w;
    if (roll < 0) return e.drop;
  }
  return weighted[weighted.length - 1]?.drop ?? null;
}

/**
 * RewardBundle 해석 — 종류별 합산 + randomModule 등급 추첨 (순수, RandomSource 주입).
 * 지급(economy/inventory/rebirth 반영)은 호출자(GameScene.grantResolvedReward) 책임.
 * artifact는 미지급 — artifacts 배열에만 담는다(유물 시스템 미구현).
 */
export function resolveRewards(bundle: RewardBundle, ctx: ResolveContext): ResolvedReward {
  const out: ResolvedReward = {
    gold: BigNumber.ZERO,
    diamond: BigNumber.ZERO,
    rebirthPoint: 0,
    upgradeStone: 0,
    moduleDrops: [],
    artifacts: [],
  };
  for (const spec of bundle) {
    switch (spec.kind) {
      case "gold":
        out.gold = out.gold.add(BigNumber.fromString(spec.amount));
        break;
      case "diamond":
        out.diamond = out.diamond.add(BigNumber.fromString(spec.amount));
        break;
      case "rebirthPoint":
        out.rebirthPoint += Math.max(0, Math.floor(spec.amount));
        break;
      case "upgradeStone":
        out.upgradeStone += Math.max(0, Math.floor(spec.amount));
        break;
      case "randomModule": {
        const pool = ctx.rarityDefs.filter((d) => (spec.rarityWeights[d.rarity] ?? 0) > 0);
        for (let i = 0; i < Math.max(1, spec.count); i++) {
          const drop = pickWeightedModule(pool, spec.rarityWeights, ctx.rng);
          if (drop) out.moduleDrops.push(drop);
        }
        break;
      }
      case "artifact":
        out.artifacts.push({ id: spec.artifactId, amount: Math.max(0, Math.floor(spec.amount)) });
        break;
    }
  }
  return out;
}
